import { ListRestart, Sparkles, TriangleAlert } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

import { useWorkflows } from "../context/WorkflowsContext";
import { writtenItems, type WorkflowFacts } from "../status";
import { FirmDefaultsLink } from "./FirmDefaultsLink";

export const START_OVER_COPY =
  "Run Everything starts over. It reorganizes every file, including ones already in folders, may rename or merge folders and remove empty or placeholder ones, renumbers files, retitles every file, replacing existing titles, and regenerates drafts, forms and the packet, replacing edits made since they were created.";

export const CASE_PREP_LINK_COPY = "Settings: Case Preparation Preferences";

export const ASK_EVERY_COPY =
  "Ask Before Every Change is on, so this run will pause at each change. An admin can turn it off in Settings > Agents.";

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** What a start-over would touch, from what the case has now. */
const replaced = (facts: WorkflowFacts): string[] => {
  const letters = writtenItems(facts.letters).length;
  const forms = writtenItems(facts.forms).length;
  return [
    facts.folders
      ? `${plural(facts.files, "file")} in ${plural(facts.folders, "folder")}`
      : null,
    letters ? plural(letters, "draft") : null,
    forms ? plural(forms, "form") : null,
    writtenItems(facts.packet ? [facts.packet] : []).length
      ? "the packet"
      : null
  ].filter((s): s is string => Boolean(s));
};

export type RunAllMode = "everything" | "remaining";

/**
 * Run Everything (all eight, in order, without stopping) or Run Remaining
 * (the steps not done): its label, whether it can run now, and whether it
 * confirms first (a start-over on a case with work, or when the firm asks
 * before every change).
 */
export const useRunAll = (mode: RunAllMode) => {
  const w = useWorkflows();
  const count = w.remaining.length;
  const everything = mode === "everything";
  const startOver = everything && w.priorWork;
  return {
    count,
    everything,
    startOver,
    confirm: startOver || w.settings.askEveryWrite,
    label: everything ? "Run Everything" : `Run Remaining (${count})`,
    testId: everything ? "workflow-run-everything" : "workflow-run-remaining",
    disabled: w.facts.files === 0 || w.running,
    go: () => (everything ? w.runEverything(startOver) : w.runRemaining())
  };
};

export const RunAllIcon: FC<{ mode: RunAllMode; accent?: boolean }> = ({
  mode,
  accent = false
}) =>
  mode === "everything" ? (
    <Sparkles className={accent ? "text-accent-seed" : undefined} />
  ) : (
    <ListRestart />
  );

/** The confirmation a run shows before it starts, inside a popover. */
export const RunAllConfirm: FC<{ mode: RunAllMode; onClose: () => void }> = ({
  mode,
  onClose
}) => {
  const w = useWorkflows();
  const run = useRunAll(mode);
  const { startOver, count, label, testId } = run;
  const touched = replaced(w.facts);
  return (
    <PopoverContent
      align="end"
      className="w-96"
      data-testid={`${testId}-confirm`}
    >
      <span className="text-base font-medium text-fg">
        {startOver ? "Start Over?" : label}
      </span>
      {startOver ? (
        <>
          <p className="text-sm text-fg-2">{START_OVER_COPY}</p>
          {touched.length ? (
            <p className="text-sm text-fg-3">
              On this case now: {touched.join(", ")}.
            </p>
          ) : null}
        </>
      ) : null}
      {w.settings.askEveryWrite ? (
        <p className="flex items-start gap-1.5 text-sm text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {ASK_EVERY_COPY}
        </p>
      ) : null}
      <FirmDefaultsLink
        label={CASE_PREP_LINK_COPY}
        className="-mx-1.5 self-start"
        testId="workflow-run-case-prep"
      />
      <div className="flex flex-wrap items-center justify-end gap-1.5 pt-1">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        {startOver && count > 0 ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              onClose();
              w.runRemaining();
            }}
            data-testid="workflow-only-left"
          >
            Only Run What&apos;s Left ({count})
          </Button>
        ) : null}
        <Button
          variant={
            startOver ? (count > 0 ? "secondary" : "primary") : "primary"
          }
          size="sm"
          className={startOver && count > 0 ? "text-danger" : undefined}
          onClick={() => {
            onClose();
            run.go();
          }}
          data-testid={`${testId}-go`}
        >
          {startOver ? "Start Over" : label}
        </Button>
      </div>
    </PopoverContent>
  );
};

type Props = {
  mode: RunAllMode;
  size?: "xs" | "sm" | "md";
  variant?: "primary" | "secondary";
};

/**
 * Run Everything or Run Remaining as a button, hidden while a run goes. On a
 * fresh case either runs at once; otherwise it confirms in a popover off the
 * button (see `useRunAll`).
 */
export const RunAllButton: FC<Props> = ({ mode, size = "sm", variant }) => {
  const w = useWorkflows();
  const run = useRunAll(mode);
  const [open, setOpen] = useState(false);
  if (w.running) return null;
  if (mode === "remaining" && run.count === 0) return null;

  const face = variant ?? (run.everything ? "primary" : "secondary");
  const button = (
    <Button
      variant={face}
      size={size}
      disabled={run.disabled}
      onClick={run.confirm ? undefined : run.go}
      data-testid={run.testId}
    >
      <RunAllIcon mode={mode} accent={face !== "primary"} />
      {run.label}
    </Button>
  );

  if (!run.confirm) return button;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <RunAllConfirm mode={mode} onClose={() => setOpen(false)} />
    </Popover>
  );
};
